import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import MomentsCaptureCard from './MomentsCaptureCard'
import MomentsReactions from './MomentsReactions'
import MomentsComments from './MomentsComments'
import { useMomentInteractions } from '../hooks/useMomentInteractions'

// Shared reveal block — stacks the pair's two captures with reactions
// + comments under each. Used by both MomentsPage (today's pair),
// MomentsCapturePage (post-submit reveal), and MomentsDayPage (history).
//
// Visibility rules driven by props, not internal logic:
//   - `mine` always shown when present
//   - `partner` shown if the row exists (RLS hides it pre-submit), else
//     either the "waiting" or "partner missed" placeholder depending
//     on whether the window is still open
//
// `readOnly` (default false) hides the compose input + reaction bar —
// used by the archive day view so old reveals are pure recall, not
// re-engagement surfaces.
export default function MomentsReveal({
  mine,
  partner,
  myImageUrl,
  partnerImageUrl,
  partnerName,
  windowOpen,
  viewerId,
  locale = 'en',
  readOnly = false,
  aiCaption = null,
  aiCaptionAr = null,
  aiTags = null,
}) {
  const { t } = useTranslation()
  const caption = locale === 'ar' ? (aiCaptionAr || aiCaption) : (aiCaption || aiCaptionAr)
  const tags = Array.isArray(aiTags) ? aiTags : null

  const captureIds = useMemo(
    () => [mine?.id, partner?.id].filter(Boolean),
    [mine?.id, partner?.id],
  )
  const { reactionsByCapture, commentsByCapture, addReaction, addComment } =
    useMomentInteractions(captureIds)

  const Cap = ({ capture, label, imageUrl }) => (
    <div className="space-y-1">
      <MomentsCaptureCard label={label} imageUrl={imageUrl} late={capture?.late} />
      {!readOnly && capture && (
        <>
          <MomentsReactions
            captureId={capture.id}
            viewerId={viewerId}
            reactions={reactionsByCapture[capture.id] || []}
            onReact={addReaction}
          />
          <MomentsComments
            captureId={capture.id}
            viewerId={viewerId}
            comments={commentsByCapture[capture.id] || []}
            locale={locale}
            onSubmit={addComment}
          />
        </>
      )}
      {readOnly && capture && (commentsByCapture[capture.id]?.length > 0 || reactionsByCapture[capture.id]?.length > 0) && (
        <div className="space-y-1.5 text-xs opacity-80">
          {(reactionsByCapture[capture.id] || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {(reactionsByCapture[capture.id] || []).map((r) => (
                <span key={r.id} className="rounded-full bg-neutral-100 px-2 py-0.5 dark:bg-neutral-800">{r.emoji}</span>
              ))}
            </div>
          )}
          {(commentsByCapture[capture.id] || []).map((c) => (
            <p key={c.id} dir="auto" className="leading-snug">{c.body}</p>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="space-y-4">
      {caption && (
        <div className="rounded-2xl bg-rose-500/10 px-4 py-3">
          <p dir="auto" className="text-sm leading-snug">{caption}</p>
          {tags && tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {tags.map((tag) => (
                <span key={tag} dir="auto" className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] uppercase tracking-wide text-rose-600 dark:text-rose-300">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
      {mine && <Cap capture={mine} label={t('moments.you')} imageUrl={myImageUrl} />}
      {partner ? (
        <Cap capture={partner} label={partnerName} imageUrl={partnerImageUrl} />
      ) : mine ? (
        <div className="grid aspect-square place-items-center rounded-2xl bg-neutral-200 dark:bg-neutral-800">
          <p dir="auto" className="px-6 text-center text-sm opacity-70">
            {windowOpen
              ? t('moments.waitingForPartner', { name: partnerName })
              : t('moments.partnerMissed', { name: partnerName })}
          </p>
        </div>
      ) : null}
    </div>
  )
}
